import React, { useState, useEffect } from 'react';
import CustomSelect from '@/components/common/CustomSelect';
import Button from '@/components/common/Button';
import { useSucursal } from '@/context/SucursalContext';
import * as serviciosApiService from '@/services/serviciosApiService';
import { FaWrench, FaFileAlt, FaTags, FaClock, FaShieldAlt, FaDollarSign, FaMapMarkerAlt, FaStickyNote } from 'react-icons/fa';
import Switch from '@/components/common/Switch';
import toastService from '@/utils/toastService';
import { normalizeDataObject } from '@/utils/normalizationUtils';
import ValidatedInput from '@/components/common/ValidatedInput';

/**
 * Formulario para crear o editar servicios con detalles por sucursal
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.initialData - Datos iniciales del servicio (para edición)
 * @param {Array} props.categorias - Lista de categorías disponibles
 * @param {function} props.onSubmit - Función a ejecutar al enviar el formulario
 * @param {function} props.onCancel - Función a ejecutar al cancelar
 * @param {function} props.onStatusChange - Función a ejecutar cuando cambia el estado
 * @returns {JSX.Element} - Componente de formulario de servicio
 */
const ServicioForm = ({ 
  initialData = {}, 
  categorias = [],
  onSubmit, 
  onCancel,
  onStatusChange 
}) => {
  // Obtener la sucursal seleccionada del contexto
  const { selectedSucursal } = useSucursal();
  
  // Datos base del servicio
  const [formData, setFormData] = useState({
    Nombre: '',
    Descripcion: '',
    Categoria: '', // Mantener para compatibilidad
    categoria_id: '', // Nuevo campo para ID de categoría
    TiempoEstimado: '',
    Garantia: '',
    SucursalId: selectedSucursal
  });

  // Detalle para la sucursal seleccionada
  const [detallesSucursal, setDetallesSucursal] = useState({
    id: null,
    // IMPORTANTE: Usamos PrecioVenta aquí para después enviarlo como Precio
    PrecioVenta: '', 
    Disponible: true,
    Notas: ''
  });

  // Estado para mostrar errores de validación
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Si hay datos iniciales, actualizar el estado del formulario
  useEffect(() => {
    if (initialData && Object.keys(initialData).length) {
      
      // Normalizar datos iniciales para tener consistencia
      const normalizedData = normalizeDataObject(initialData, 'servicio');
      
      // Datos base del servicio
      setFormData({
        Nombre: normalizedData.Nombre || normalizedData.nombre || '',
        Descripcion: normalizedData.Descripcion || normalizedData.descripcion || '',
        Categoria: normalizedData.Categoria || '', // Mantener para compatibilidad
        categoria_id: normalizedData.categoria_id || normalizedData.CategoriaId || '', // Usar ID de categoría si existe
        TiempoEstimado: normalizedData.TiempoEstimado || '',
        Garantia: normalizedData.Garantia || '',
      });
      
      // Buscar detalle para la sucursal seleccionada
      if (selectedSucursal) {
        
        // Verificar las diferentes posibilidades de estructura de datos
        let precioEncontrado = null;
        let notasEncontradas = '';
        let disponible = true;
        let detalleId = null;
        
        // 1. DetallesPorSucursal como array
        if (initialData.DetallesPorSucursal && Array.isArray(initialData.DetallesPorSucursal)) {
          const detalleSucursalActual = initialData.DetallesPorSucursal.find(detalle => {
            // Buscar por SucursalId directo
            if (detalle.SucursalId === selectedSucursal) return true;
            
            // Buscar en propiedad Sucursal como array
            if (detalle.Sucursal && Array.isArray(detalle.Sucursal) && 
                detalle.Sucursal.includes(selectedSucursal)) return true;
                
            // Buscar en propiedad Sucursal directa
            return detalle.Sucursal === selectedSucursal;
          });
          
          if (detalleSucursalActual) {
            precioEncontrado = detalleSucursalActual.Precio || detalleSucursalActual.PrecioVenta || null;
            notasEncontradas = detalleSucursalActual.Notas || '';
            disponible = detalleSucursalActual.Disponible !== false;
            detalleId = detalleSucursalActual.id || null;
          }
        }
        
        // 2. Verificar ServicioPorSucursalId
        if (!precioEncontrado && initialData.ServicioPorSucursalId) {
          precioEncontrado = initialData.Precio;
          notasEncontradas = initialData.Notas || '';
          disponible = initialData.Disponible !== false;
          detalleId = initialData.ServicioPorSucursalId;
        }
        
        // 3. Verificar si los datos están directamente en el servicio
        if (!precioEncontrado && initialData.Precio !== undefined) {
          precioEncontrado = initialData.Precio;
          notasEncontradas = initialData.Notas || '';
          disponible = initialData.Disponible !== false;
        }
        
        setDetallesSucursal({
          id: detalleId,
          PrecioVenta: precioEncontrado !== null ? precioEncontrado : '',
          Disponible: disponible,
          Notas: notasEncontradas
        });
      } else {
        // Si no hay sucursal seleccionada, usar valores por defecto
        setDetallesSucursal({
          id: null,
          PrecioVenta: '',
          Disponible: true,
          Notas: ''
        });
      }
    }
  }, [initialData, selectedSucursal]);

  // Manejar cambios en los datos base del servicio
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    // Para campos numéricos, convertir a número
    if (type === 'number') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value ? Number(value) : '' 
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Manejar cambios en los detalles de la sucursal
  const handleDetalleChange = (field, value) => {
    setDetallesSucursal(prev => {
      if (field === 'Disponible') {
        // Para checkbox, convertir a booleano
        return {
          ...prev,
          [field]: value === 'true' || value === true
        };
      } else if (field === 'PrecioVenta') {
        // Para precio, convertir a número si tiene valor
        return {
          ...prev,
          [field]: value ? Number(value) : ''
        };
      } else {
        // Para otros campos, mantener el valor como está
        return {
          ...prev,
          [field]: value
        };
      }
    });
  };

  // Manejar toggle del estado disponible/no disponible
  const handleToggleDisponible = async (newDisponible) => {
    // Solo permitir toggle si estamos editando (hay initialData con id)
    if (!initialData || !initialData.id) {
      return;
    }

    // Si no se pasa parámetro, usar el valor actual invertido
    if (typeof newDisponible !== 'boolean') {
      newDisponible = !detallesSucursal.Disponible;
    }
    
    try {
      // Actualizar inmediatamente en la base de datos
      await serviciosApiService.updateServicio(initialData.id, {
        Disponible: newDisponible,
        activo: newDisponible
      });
      
      // Actualizar el estado local
      setDetallesSucursal(prev => ({ ...prev, Disponible: newDisponible }));
      
      // Mostrar mensaje de éxito
      toastService.success(`Servicio ${newDisponible ? 'activado' : 'desactivado'} correctamente`);
      
      // Llamar callback para refrescar la lista
      if (onStatusChange) {
        onStatusChange();
      }
      
    } catch (error) {
      console.error('Error al actualizar estado del servicio:', error);
      toastService.error('Error al actualizar el estado del servicio');
    }
  };

  // Función para validar todos los campos requeridos
  const validateAllFields = () => {
    let hasErrors = false;
    
    // Mostrar errores de validación
    setShowValidationErrors(true);
    
    // Validar nombre del servicio
    if (!formData.Nombre || formData.Nombre.trim() === '') {
      hasErrors = true;
    }
    
    // Validar categoría
    if (!formData.categoria_id || formData.categoria_id === '') {
      hasErrors = true;
    }
    
    // Validar tiempo estimado  
    if (!formData.TiempoEstimado || formData.TiempoEstimado === '') {
      hasErrors = true;
    }
    
    // Validar que haya una sucursal seleccionada
    if (!selectedSucursal) {
      toastService.error("Por favor, selecciona una sucursal en el menú lateral.");
      hasErrors = true;
    }
    
    // Validar precio (solo si hay sucursal seleccionada)
    if (selectedSucursal && (!detallesSucursal.PrecioVenta && detallesSucursal.PrecioVenta !== 0)) {
      hasErrors = true;
    }
    
    return !hasErrors;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Validar todos los campos
    if (!validateAllFields()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Preparar datos para envío
      const dataToSend = {
        ...formData,
        // Asegurar que se envíe el categoria_id si está disponible
        categoria_id: formData.categoria_id || null,
        DetallesSucursal: {
          ...detallesSucursal,
          SucursalId: selectedSucursal,
          // IMPORTANTE: Cambiar PrecioVenta a Precio para el envío
          Precio: detallesSucursal.PrecioVenta
        }
      };

      // Normalizar datos antes de enviar
      const normalizedDataToSend = normalizeDataObject(dataToSend, 'servicio');

      
      // Enviar datos completos
      await onSubmit(normalizedDataToSend);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <form onSubmit={handleSubmit} className="text-white">

      <div className="md:grid md:grid-cols-2 md:gap-6">
        {/* Columna izquierda: Información Básica */}
        <div className="bg-gray-800 p-4 rounded-lg mb-6 md:mb-0 border border-gray-600">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaWrench className="text-blue-400 mr-2" />
            Información Básica
          </h3>
          
          {/* Nombre del servicio */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaWrench className="text-gray-400 mr-2 text-xs" />
              Nombre del servicio<span className="text-red-500 ml-1">*</span>
            </label>
            <ValidatedInput
              type="text"
              name="Nombre"
              placeholder="Ej: Cambio de aceite y filtro"
              value={formData.Nombre}
              onChange={handleChange}
              className="w-full"
              required
              requiredMessage="El nombre del servicio es obligatorio"
              forceShowValidation={showValidationErrors}
            />
          </div>
          
          {/* Descripción */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaFileAlt className="text-gray-400 mr-2 text-xs" />
              Descripción
            </label>
            <textarea
              name="Descripcion"
              placeholder="Describe en qué consiste el servicio..."
              value={formData.Descripcion}
              onChange={handleChange}
              className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              rows={3}
            />
          </div>
          
          {/* Categoría y Precio en la misma fila */}
          {selectedSucursal && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              {/* Categoría */}
              <div className="sm:col-span-2">
                <label className="flex items-center text-sm font-medium mb-2">
                  <FaTags className="text-gray-400 mr-2 text-xs" />
                  Categoría<span className="text-red-500 ml-1">*</span>
                </label>
                <CustomSelect
                  name="categoria_id"
                  value={formData.categoria_id}
                  onChange={handleChange}
                  placeholder="Seleccionar categoría"
                  options={categorias
                    .sort((a, b) => a.nombre.localeCompare(b.nombre))
                    .map(categoria => ({
                      id: categoria.id,
                      value: categoria.id,
                      label: categoria.nombre
                    }))}
                  className="w-full"
                  required
                  requiredMessage="La categoría es requerida"
                  forceShowValidation={showValidationErrors}
                />
              </div>
              
              {/* Precio */}
              <div>
                <label className="flex items-center text-sm font-medium mb-2">
                  <FaDollarSign className="text-gray-400 mr-2 text-xs" />
                  Precio<span className="text-red-500 ml-1">*</span>
                </label>
                <ValidatedInput
                  type="number"
                  name="PrecioVenta"
                  value={detallesSucursal.PrecioVenta}
                  onChange={(e) => handleDetalleChange('PrecioVenta', e.target.value)}
                  min="0"
                  placeholder="0"
                  required
                  requiredMessage="Requerido"
                  forceShowValidation={showValidationErrors}
                />
              </div>
            </div>
          )}
          
          {/* Categoría sola cuando no hay sucursal */}
          {!selectedSucursal && (
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium mb-2">
                <FaTags className="text-gray-400 mr-2 text-xs" />
                Categoría<span className="text-red-500 ml-1">*</span>
              </label>
              <CustomSelect
                name="categoria_id"
                value={formData.categoria_id}
                onChange={handleChange}
                placeholder="Seleccionar categoría"
                options={categorias
                  .sort((a, b) => a.nombre.localeCompare(b.nombre))
                  .map(categoria => ({
                    id: categoria.id,
                    value: categoria.id,
                    label: categoria.nombre
                  }))}
                className="w-full"
                required
                requiredMessage="La categoría es requerida"
                forceShowValidation={showValidationErrors}
              />
            </div>
          )}
          
        </div>
        
        {/* Columna derecha: Detalles del Servicio */}
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaClock className="text-yellow-400 mr-2" />
            Detalles del Servicio
          </h3>
          
          {/* Tiempo estimado y Garantía en la misma fila */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaClock className="text-gray-400 mr-2 text-xs" />
                Tiempo (min)<span className="text-red-500 ml-1">*</span>
              </label>
              <ValidatedInput
                type="number"
                name="TiempoEstimado"
                placeholder="Duración"
                value={formData.TiempoEstimado}
                onChange={handleChange}
                className="w-full"
                min="0"
                required
                requiredMessage="El tiempo es requerido"
                forceShowValidation={showValidationErrors}
              />
            </div>
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaShieldAlt className="text-gray-400 mr-2 text-xs" />
                Garantía (días)
              </label>
              <input
                type="number"
                name="Garantia"
                placeholder="Días"
                value={formData.Garantia}
                onChange={handleChange}
                className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                min="0"
              />
            </div>
          </div>
          
          {/* Notas específicas */}
          {selectedSucursal && (
            <div className="border-t border-gray-600 pt-4">
              <div>
                <label className="flex items-center text-sm font-medium mb-2">
                  <FaStickyNote className="text-gray-400 mr-2 text-xs" />
                  Notas específicas
                </label>
                <textarea
                  value={detallesSucursal.Notas}
                  onChange={(e) => handleDetalleChange('Notas', e.target.value)}
                  className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  rows={3}
                  placeholder="Ej: Solo disponible con cita previa"
                />
              </div>
            </div>
          )}
          
          {!selectedSucursal && (
            <div className="border-t border-gray-600 pt-4">
              <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-800/50">
                <p className="text-blue-200 text-sm flex items-center">
                  <FaMapMarkerAlt className="text-blue-400 mr-2 text-sm" />
                  Por favor, selecciona una sucursal en el menú lateral para configurar los detalles del servicio.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Separador visual y botones */}
      <div className="border-t border-gray-600 pt-6 mt-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          {/* Switch de disponibilidad */}
          {selectedSucursal && (
            <div className="order-1 sm:order-1">
              <Switch
                checked={detallesSucursal.Disponible}
                onChange={handleToggleDisponible}
                activeLabel="Servicio disponible"
                inactiveLabel="Servicio no disponible"
                size="md"
              />
            </div>
          )}
          
          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-3 order-2 sm:order-2 md:justify-end">
            <Button 
              variant="secondary" 
              onClick={onCancel}
              className="w-full sm:w-auto py-2.5 px-6 text-sm font-medium order-1"
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="button"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!selectedSucursal || isSubmitting}
              className="w-full sm:w-auto py-2.5 px-6 text-sm font-medium order-2"
            >
              {initialData.id ? 'Actualizar' : 'Crear'} Servicio
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ServicioForm;